import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, ArrowUpRight, BatteryFull, BatteryMedium, BatteryLow, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { DeviceRowsSkeleton } from "../components/PageSkeletons";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PendingSyncBadge from "../components/PendingSyncBadge";
import { useConfirm } from "../components/ConfirmDialog";
import { useAppNames } from "../hooks/use-app-names";

const batteryMeta = (b) => {
  if (b == null) return { Icon: BatteryMedium, tone: "text-neutral-400", label: "—" };
  if (b > 50) return { Icon: BatteryFull, tone: "text-green-700", label: `${b}%` };
  if (b > 20) return { Icon: BatteryMedium, tone: "text-amber-600", label: `${b}%` };
  return { Icon: BatteryLow, tone: "text-red-600", label: `${b}%` };
};

const timeAgo = (iso) => {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "—";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

export default function Devices() {
  const confirm = useConfirm();
  const { byId: appsById, reload: reloadApps } = useAppNames();
  const [employees, setEmployees] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [unlinkedDevices, setUnlinkedDevices] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ serial_number: "", app_id: "", department: "" });
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [namingApp, setNamingApp] = useState(null);
  const [nameAppValue, setNameAppValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [renameBusy, setRenameBusy] = useState(false);
  const [nameAppBusy, setNameAppBusy] = useState(false);

  const load = () => {
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => setEmployees([]));
    api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {});
    api.get("/devices").then(({ data }) => setUnlinkedDevices(data)).catch(() => setUnlinkedDevices([]));
  };
  useEffect(() => {
    load();
    // Background retries land on the server every 60s -- poll so a "Pending sync"
    // badge disappears on its own once one succeeds, no manual refresh needed.
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, []);

  const assigned = employees?.filter((e) => e.device) || [];
  const unassigned = employees?.filter((e) => !e.device) || [];

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({ serial_number: emp.device.serial_number, app_id: emp.device.app_id, department: emp.department });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/employees/${editing.id}/device`, form);
      if (data.metadata_sync?.status === "synced") {
        toast.success("Device updated", { description: "Synced to local server" });
      } else {
        toast.success("Device updated", { description: "Saved — will sync once the local server is back online" });
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const openRename = (device) => {
    setRenaming(device);
    setRenameValue(device.device_name === "Unnamed device" ? "" : (device.device_name || ""));
  };

  const saveRename = async (e) => {
    e.preventDefault();
    setRenameBusy(true);
    try {
      await api.put(`/devices/${renaming.id}`, { device_name: renameValue });
      toast.success("Device renamed");
      setRenaming(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRenameBusy(false);
    }
  };

  const saveNameApp = async (e) => {
    e.preventDefault();
    setNameAppBusy(true);
    try {
      await api.put(`/apps/${namingApp}/name`, { app_name: nameAppValue });
      toast.success("App named");
      setNamingApp(null);
      reloadApps();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setNameAppBusy(false);
    }
  };

  const openNameApp = (appId, currentName) => {
    setNamingApp(appId);
    setNameAppValue(currentName || "");
  };

  // Both device tables show an App ID column -- an unnamed app_id (identified
  // once via Phase 1's /identify but never named) gets a "Name this app"
  // action instead of the raw id; a named one shows app_name everywhere the
  // raw id would otherwise appear, per Phase 4. Either way the cell itself is
  // the click target that opens the naming popup -- no separate edit button.
  const AppIdCell = ({ appId }) => {
    if (!appId) return <span>—</span>;
    const app = appsById[appId];
    if (app?.app_name) {
      return (
        <button data-testid={`name-app-${appId}`} onClick={() => openNameApp(appId, app.app_name)}
          className="hover:underline underline-offset-4">
          {app.app_name}
        </button>
      );
    }
    return (
      <button data-testid={`name-app-${appId}`} onClick={() => openNameApp(appId, "")}
        className="inline-flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 hover:bg-amber-100 transition-colors">
        Name this app
      </button>
    );
  };

  const deleteDevice = async (device) => {
    const ok = await confirm({
      title: `Delete ${device.device_name || device.serial_number || "this device"}?`,
      description: "Permanently removes this device record. It hasn't been linked to an employee, so nothing else references it.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/devices/${device.id}`);
      toast.success("Device deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div>
        <h1 className="font-heading font-light text-3xl tracking-tight" data-testid="devices-title">Devices</h1>
        <p className="text-sm text-neutral-500 mt-1">EXOPILOT pendants recording via BLE to the paired companion phone app.</p>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-x-auto">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between min-w-[860px]">
          <h2 className="text-sm font-medium">Assigned pendants ({assigned.length})</h2>
          <span className="text-[11px] text-neutral-400 font-mono-data">Battery & last-seen reported by the companion phone app</span>
        </div>
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-neutral-200">
              <th className="px-6 py-3 font-medium">Device name</th>
              <th className="px-4 py-3 font-medium">App ID</th>
              <th className="px-4 py-3 font-medium">Employee</th>
              <th className="px-4 py-3 font-medium">Department</th>
              <th className="px-4 py-3 font-medium">Battery</th>
              <th className="px-4 py-3 font-medium">Last seen</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {employees === null && <DeviceRowsSkeleton />}
            {assigned.map((emp) => {
              const bat = batteryMeta(emp.device.battery);
              return (
              <tr key={emp.id} data-testid={`device-row-${emp.device.serial_number}`} className="hover:bg-neutral-50 transition-colors">
                <td className="px-6 py-4">
                  <span className="inline-flex items-center gap-2 font-mono-data text-xs">
                    <Radio className="w-3.5 h-3.5 text-green-600" />
                    {emp.device.device_name && emp.device.device_name !== "Unnamed device"
                      ? emp.device.device_name
                      : emp.device.serial_number}
                  </span>
                </td>
                <td className="px-4 py-4 text-xs text-neutral-600"><AppIdCell appId={emp.device.app_id} /></td>
                <td className="px-4 py-4">
                  <Link to={`/console/employees/${emp.id}`} className="inline-flex items-center gap-1 hover:underline underline-offset-4">
                    {emp.name} <ArrowUpRight className="w-3 h-3 text-neutral-400" />
                  </Link>
                </td>
                <td className="px-4 py-4 text-neutral-600">
                  <div className="flex items-center gap-2">
                    <span>{emp.department}</span>
                    {emp.metadata_sync?.status === "failed" && <PendingSyncBadge testId={`device-pending-sync-${emp.device.serial_number}`} />}
                  </div>
                </td>
                <td className="px-4 py-4" data-testid={`device-battery-${emp.device.serial_number}`}>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-mono-data ${bat.tone}`}>
                    <bat.Icon className="w-4 h-4" /> {bat.label}
                  </span>
                </td>
                <td className="px-4 py-4 text-xs text-neutral-500 font-mono-data" data-testid={`device-lastseen-${emp.device.serial_number}`}>
                  {timeAgo(emp.device.last_seen)}
                </td>
                <td className="px-4 py-4">
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">Active</span>
                </td>
                <td className="px-4 py-4 text-right">
                  <button data-testid={`edit-device-${emp.device.serial_number}`} onClick={() => openEdit(emp)} title="Edit device"
                    className="p-2 rounded-md text-neutral-400 hover:text-[#111] hover:bg-neutral-100 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );})}
            {employees !== null && assigned.length === 0 && (
              <tr><td colSpan={8} className="px-6 py-10 text-center text-sm text-neutral-400">No pendants assigned yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {unassigned.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6" data-testid="unassigned-list">
          <h2 className="text-sm font-medium">Awaiting a pendant ({unassigned.length})</h2>
          <div className="flex flex-wrap gap-2 mt-4">
            {unassigned.map((emp) => (
              <Link key={emp.id} to="/console/employees"
                className="text-xs border border-neutral-300 rounded-full px-3.5 py-1.5 text-neutral-600 hover:bg-[#111] hover:text-white hover:border-[#111] transition-colors">
                {emp.name} · {emp.department}
              </Link>
            ))}
          </div>
        </div>
      )}

      {unlinkedDevices?.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-x-auto" data-testid="unlinked-devices-list">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between min-w-[720px]">
            <h2 className="text-sm font-medium">Synced pendants ({unlinkedDevices.length})</h2>
            <span className="text-[11px] text-neutral-400 font-mono-data">Discovered from the companion app, not yet linked to an employee</span>
          </div>
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-neutral-200">
                <th className="px-6 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">MAC / serial</th>
                <th className="px-4 py-3 font-medium">App ID</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {unlinkedDevices.map((d) => {
                const unnamed = !d.device_name || d.device_name === "Unnamed device";
                return (
                  <tr key={d.id} data-testid={`unlinked-device-${d.id}`} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-6 py-4">
                      <button data-testid={`rename-device-${d.id}`} onClick={() => openRename(d)} title="Rename device"
                        className="inline-flex items-center gap-2">
                        <Radio className="w-3.5 h-3.5 text-neutral-400" />
                        {unnamed ? (
                          <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 hover:bg-amber-100 transition-colors">
                            Unnamed device
                          </span>
                        ) : (
                          <span className="hover:underline underline-offset-4">{d.device_name}</span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-4 font-mono-data text-xs text-neutral-600">{d.device_address || d.serial_number}</td>
                    <td className="px-4 py-4 text-xs text-neutral-600"><AppIdCell appId={d.app_id} /></td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <button data-testid={`delete-device-${d.id}`} onClick={() => deleteDevice(d)} title="Delete device"
                        className="p-2 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-device-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-medium">Edit device</DialogTitle>
            <DialogDescription>{editing?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2 min-w-0">
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Serial number</label>
              <input data-testid="edit-device-serial-input" required value={form.serial_number}
                onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#111]" />
              <p className="text-[11px] text-neutral-400 mt-1">Must be unique across all devices.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">App ID</label>
              <input data-testid="edit-device-appid-input" required value={form.app_id}
                onChange={(e) => setForm({ ...form, app_id: e.target.value })}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#111]" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Department</label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })} required>
                <SelectTrigger data-testid="edit-device-department-select" className="mt-1.5">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <button data-testid="edit-device-submit-button" type="submit" disabled={busy}
              className="w-full bg-[#111] text-white rounded-full py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="sm:max-w-md" data-testid="rename-device-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-medium">Rename device</DialogTitle>
            <DialogDescription className="font-mono-data text-xs">{renaming?.device_address || renaming?.serial_number}</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveRename} className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Name</label>
              <input data-testid="rename-device-input" required value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111]" placeholder="Front Desk Pendant" />
            </div>
            <button data-testid="rename-device-submit-button" type="submit" disabled={renameBusy}
              className="w-full bg-[#111] text-white rounded-full py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {renameBusy ? "Saving…" : "Save name"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!namingApp} onOpenChange={(o) => !o && setNamingApp(null)}>
        <DialogContent className="sm:max-w-md" data-testid="name-app-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-medium">Name this app</DialogTitle>
            <DialogDescription className="font-mono-data text-xs">{namingApp}</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveNameApp} className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Name</label>
              <input data-testid="name-app-input" required value={nameAppValue}
                onChange={(e) => setNameAppValue(e.target.value)}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111]" placeholder="Front Desk Tablet" />
              <p className="text-[11px] text-neutral-400 mt-1">Shown wherever this app would otherwise appear as a raw ID.</p>
            </div>
            <button data-testid="name-app-submit-button" type="submit" disabled={nameAppBusy}
              className="w-full bg-[#111] text-white rounded-full py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {nameAppBusy ? "Saving…" : "Save name"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
